// src/pages/PatientLifestylePage.jsx
// Route: /patient/lifestyle
//
// A "Lifestyle" tab (MyHealthTabs) right after Markers — the AI lifestyle
// plan (LifestyleRecommendationService, "Layer 3") already exists and is
// already shown to navigators inside the per-record triage report they open
// from Health Records (TriageReportModal.js's LifestyleTab). Until this page
// existed, a patient had no way to see their own plan — only staff could
// open the report modal it lives in.
//
// Reuses LifestyleTab directly rather than rebuilding it, the same "share
// one implementation across portals" pattern EffectivePanelTab already
// uses for Current Markers (patientView prop) and DoctorTriageView already
// uses for its own Lifestyle tab. audience="patient" swaps the disclaimer
// copy only — LifestyleTab's default text is a navigator-facing instruction
// ("share this with the patient during the call"), which reads as a leaked
// internal note when shown to the patient themselves.
//
// Like Current Markers, this needs the patient's most recent BLOOD_TEST
// triage — the only triage type that ever gets a lifestyle plan (see
// HealthRecordService.saveTriageResult) — resolved from the same
// triage-history endpoint PatientCurrentMarkersPage/PatientHealthTimeline
// already call, so no new backend endpoint is needed.

import React, { useEffect, useState, useCallback } from 'react';
import { PatientAppShell } from '../components/PatientLayout';
import { usePatient } from '../context/PatientContext';
import { healthRecordAPI } from '../services/api';
import { LifestyleTab, injectCss as injectTriageReportCss } from '../components/healthrecords/TriageReportModal';
import PageHead from '../components/PageHead';
import MyHealthTabs from '../components/MyHealthTabs';
import Icon from '../components/Icon';

export default function PatientLifestylePage() {
  const { patient } = usePatient();
  const [latestTriage, setLatestTriage] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadLatestTriage = useCallback(async () => {
    if (!patient?.id) return;
    try {
      const res = await healthRecordAPI.getTriageHistory(patient.id);
      const data = Array.isArray(res.data) ? res.data : res.data?.content || [];
      const bloodTests = data.filter(t => t.triageType === 'BLOOD_TEST');
      const [latest] = bloodTests.sort((a, b) =>
        new Date(b.createdAt || b.drawDate) - new Date(a.createdAt || a.drawDate));
      setLatestTriage(latest || null);
    } catch {
      // Same "failure here is just no content" pattern as the markers page —
      // never fail the whole page over this.
    } finally {
      setLoading(false);
    }
  }, [patient?.id]);

  useEffect(() => { loadLatestTriage(); }, [loadLatestTriage]);

  // LifestyleTab renders .rpt-* classes that only exist once injected —
  // TriageReportModal injects them itself when it mounts as a modal; here
  // it's reused standalone, the same way DoctorTriageView.jsx already has to.
  useEffect(() => { injectTriageReportCss(); }, []);

  return (
    <PatientAppShell breadcrumbs={[{ label: 'My Health', to: '/patient/timeline' }, { label: 'Lifestyle' }]}>
      <div className="page-shell page-shell--health">
        <MyHealthTabs />

        <PageHead
          eyebrow="My health timeline"
          title="Your lifestyle plan"
          subtitle="A short, personalised plan generated from your most recent blood test —
                    a few concrete habits, not a lecture."
        />

        {!loading && !latestTriage && (
          <div className="card">
            <div className="empty-state">
              <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="heart" size={20} /></div>
              <div className="empty-title">No lifestyle plan yet</div>
              <div className="empty-desc">
                Your personalised plan generates automatically once a blood test is processed.
              </div>
            </div>
          </div>
        )}

        {latestTriage && (
          <LifestyleTab
            triage={latestTriage}
            patientId={patient?.id}
            recordId={latestTriage.healthRecordId}
            audience="patient"
            standalone
          />
        )}
      </div>
    </PatientAppShell>
  );
}
